//pages/PrivacyPolicy.tsx
// No local loading state needed
import Layout from "@/components/Layout";
// SEO: Import SEO component for legal/privacy page meta + structured data.
import SEO from '@/components/SEO';
import { Card, CardContent } from "@/components/ui/card";
import { Shield, Mail, Globe2, MapPin, Lock } from "lucide-react";
// Removed ShimmerOverlay to eliminate loading bar/shimmer

export default function PrivacyPolicy() {
  // Remove simulated loading; render content immediately
  // JSON-LD: Use WebPage (privacy policy) schema to clarify document purpose for crawlers.
  const privacySchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': 'Privacy Policy',
    'url': 'https://tradesagepro.com/privacy',
    'description': 'Privacy Policy detailing GDPR, CCPA/CPRA, PIPEDA, LGPD compliance for TradeSagePro AI market research and data analysis platform.',
    'about': {
      '@type': 'Organization',
      'name': 'TradeSagePro'
    }
  };
  return (
    <Layout>
      {/* SEO TAGS: Privacy policy is indexable (users search for brand + privacy); structured data helps clarity. */}
      <SEO
        title="Privacy Policy | TradeSagePro AI Market Research"
        description="Read how TradeSagePro collects, processes and protects personal data under GDPR, CCPA/CPRA, PIPEDA and LGPD for its AI market research and data analysis platform."
        canonical="https://tradesagepro.com/privacy"
        type="article"
        jsonLd={privacySchema}
      />
      {/* END SEO TAGS */}
      <div className="min-h-screen bg-[hsl(226,26%,7%)] text-[hsl(210,9%,91%)] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <Shield className="w-12 h-12 text-[hsl(193,100%,50%)] mr-3" />
              <h1 className="text-4xl font-bold text-white">Privacy Policy</h1>
            </div>
            <p className="text-sm text-[hsl(210,9%,80%)]">Effective Date: October 12, 2025</p>
          </div>

          {/* Company Meta Card */}
            <Card className="bg-[hsl(215,15%,18%)] border-gray-700 mb-8">
              <CardContent className="p-6">
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-3">
                  <Globe2 className="w-4 h-4 text-[hsl(193,100%,50%)]" />
                  <div>
                    <div className="text-[hsl(210,9%,91%)]">Company</div>
                    <div className="text-white font-semibold">JINOVEX LLC</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-[hsl(193,100%,50%)]" />
                  <div>
                    <div className="text-[hsl(210,9%,91%)]">Support</div>
                    <a className="text-[hsl(193,100%,50%)] hover:underline" href="mailto:support@tradesagepro.com">support@tradesagepro.com</a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-[hsl(193,100%,50%)]" />
                  <div>
                    <div className="text-[hsl(210,9%,91%)]">Data Protection Officer (DPO)</div>
                    <a className="text-[hsl(193,100%,50%)] hover:underline" href="mailto:info@tradesagepro.com">info@tradesagepro.com</a>
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:col-span-2">
                  <MapPin className="w-4 h-4 text-[hsl(193,100%,50%)]" />
                  <div>
                    <div className="text-[hsl(210,9%,91%)]">Address</div>
                    <div className="text-white">Registered Address: JINOVEX LLC – Online Service Operations, Tbilisi, Georgia (operating globally)</div>
                  </div>
                </div>
              </div>
              </CardContent>
            </Card>

            <Card className="bg-[hsl(215,15%,18%)] border-gray-700 animate-slide-in">
              <CardContent className="p-8 max-w-none">
              <div className="space-y-10 text-[hsl(210,9%,91%)] leading-relaxed">

                <section id="intro">
                  <h2 className="text-2xl font-bold text-white mb-3">1. Introduction and Contact Information</h2>
                  <p className="mb-3">
                    TradeSagePro ("we," "us," or "our") operates the AI-powered market research and data analysis platform available at
                    tradesagepro.com (the "Platform"). We are committed to safeguarding your privacy and handling your
                    personal data transparently and in full compliance with international privacy frameworks, including:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>General Data Protection Regulation (GDPR) – EU/EEA</li>
                    <li>California Consumer Privacy Act (CCPA/CPRA) – USA, California</li>
                    <li>Personal Information Protection and Electronic Documents Act (PIPEDA) – Canada</li>
                    <li>Lei Geral de Proteção de Dados Pessoais (LGPD) – Brazil</li>
                  </ul>
                  <div className="mt-4 grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-[#0F1623] border border-[#1E2A44]">
                      <div className="flex items-center gap-2 font-semibold text-white"><Mail className="w-4 h-4" /> DPO</div>
                      <a href="mailto:info@tradesagepro.com" className="text-[hsl(193,100%,50%)] hover:underline">info@tradesagepro.com</a>
                    </div>
                    <div className="p-4 rounded-lg bg-[#0F1623] border border-[#1E2A44]">
                      <div className="flex items-center gap-2 font-semibold text-white"><Mail className="w-4 h-4" /> Support</div>
                      <a href="mailto:support@tradesagepro.com" className="text-[hsl(193,100%,50%)] hover:underline">support@tradesagepro.com</a>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-[hsl(210,9%,80%)]">If you reside in Brazil, our LGPD representative (“Encarregado”) is also reachable at the same DPO contact.</p>
                </section>

                <section id="collection">
                  <h2 className="text-2xl font-bold text-white mb-3">2. Information We Collect</h2>
                  <p className="mb-4">We collect personal data necessary for account creation, platform operation, fraud prevention, and payment processing.</p>
                  <div className="overflow-x-auto rounded-lg border border-gray-700">
                    <div className="min-w-[720px] grid grid-cols-4 text-sm">
                      <div className="bg-gray-800/60 px-4 py-3 font-semibold">Data Category</div>
                      <div className="bg-gray-800/60 px-4 py-3 font-semibold">Examples</div>
                      <div className="bg-gray-800/60 px-4 py-3 font-semibold">Purpose of Processing</div>
                      <div className="bg-gray-800/60 px-4 py-3 font-semibold">Legal Basis (GDPR/LGPD)</div>

                      <div className="px-4 py-3">Identifiers</div>
                      <div className="px-4 py-3">Email address, verification code</div>
                      <div className="px-4 py-3">Account registration and authentication</div>
                      <div className="px-4 py-3">Contractual Necessity (GDPR Art. 6(1)(b))</div>

                      <div className="px-4 py-3">Technical and Session Data</div>
                      <div className="px-4 py-3">Public IP, device type, browser, OS</div>
                      <div className="px-4 py-3">Secure session management, fraud detection, access control</div>
                      <div className="px-4 py-3">Legitimate Interest (GDPR Art. 6(1)(f))</div>

                      <div className="px-4 py-3">Behavioral / Usage Data</div>
                      <div className="px-4 py-3">Chosen symbols, AI analysis output prefs (Positive Bias/Negative Bias/Neutral), interaction logs</div>
                      <div className="px-4 py-3">Core service functionality and algorithmic improvement</div>
                      <div className="px-4 py-3">Contractual Necessity</div>

                      <div className="px-4 py-3">Financial and Transactional</div>
                      <div className="px-4 py-3">Paddle.com payment ID, credit purchase/usage</div>
                      <div className="px-4 py-3">Transaction processing; credit return on “Neutral/No Output”</div>
                      <div className="px-4 py-3">Contractual Necessity</div>

                      <div className="px-4 py-3">Cookies and Tracking</div>
                      <div className="px-4 py-3">Session cookies, analytics cookies</div>
                      <div className="px-4 py-3">Analytics, fraud prevention, marketing optimization</div>
                      <div className="px-4 py-3">Consent (GDPR Art. 6(1)(a)) where applicable</div>
                    </div>
                  </div>
                  <div className="mt-4 text-sm">
                    <p className="mb-1"><strong>CCPA Categories Collected:</strong> Identifiers, internet activity data, geolocation (inferred from IP), and commercial information.</p>
                    <p className="text-[hsl(210,9%,80%)]">PIPEDA Notice: We collect, use, and disclose information only for purposes a reasonable person would consider appropriate in the given circumstances.</p>
                  </div>
                </section>

                <section id="legal-basis">
                  <h2 className="text-2xl font-bold text-white mb-3">3. Legal Bases for Processing and Purpose</h2>
                  <ul className="list-disc pl-6 space-y-1">
                    <li><strong>Contractual necessity</strong> – To register and provide you access to the TradeSagePro platform.</li>
                    <li><strong>Legitimate interest</strong> – To ensure security, prevent fraud, and maintain system integrity.</li>
                    <li><strong>Consent</strong> – For optional cookies and analytics features.</li>
                    <li><strong>Legal obligation</strong> – To comply with tax, payment, and accounting laws.</li>
                  </ul>
                  <p className="mt-3 text-sm text-[hsl(210,9%,80%)]">Under the CCPA/CPRA, processing aligns with permissible “business purposes.” Under PIPEDA, processing occurs only with consent and for purposes directly related to service provision.</p>
                </section>

                <section id="sharing">
                  <h2 className="text-2xl font-bold text-white mb-3">4. Data Sharing and Third Parties</h2>
                  <p className="mb-3">We share data only when strictly necessary:</p>
                  <div className="overflow-x-auto rounded-lg border border-gray-700">
                    <div className="min-w-[720px] grid grid-cols-4 text-sm">
                      <div className="bg-gray-800/60 px-4 py-3 font-semibold">Recipient Type</div>
                      <div className="bg-gray-800/60 px-4 py-3 font-semibold">Example</div>
                      <div className="bg-gray-800/60 px-4 py-3 font-semibold">Purpose</div>
                      <div className="bg-gray-800/60 px-4 py-3 font-semibold">Safeguards</div>

                      <div className="px-4 py-3">Payment Processor</div>
                      <div className="px-4 py-3">Paddle.com</div>
                      <div className="px-4 py-3">Secure transaction processing and fraud prevention</div>
                      <div className="px-4 py-3">DPA & PCI-DSS compliance</div>

                      <div className="px-4 py-3">Hosting Provider</div>
                      <div className="px-4 py-3">Cloud infrastructure providers</div>
                      <div className="px-4 py-3">Platform operation and storage</div>
                      <div className="px-4 py-3">Standard Contractual Clauses (SCCs)</div>

                      <div className="px-4 py-3">Analytics Provider</div>
                      <div className="px-4 py-3">Google Analytics (where consented)</div>
                      <div className="px-4 py-3">Usage analysis and performance metrics</div>
                      <div className="px-4 py-3">IP anonymization; opt-out available</div>

                      <div className="px-4 py-3">Legal Authorities</div>
                      <div className="px-4 py-3">Regulatory or judicial bodies</div>
                      <div className="px-4 py-3">Compliance with applicable law</div>
                      <div className="px-4 py-3">Disclosure limited to legal necessity</div>
                    </div>
                  </div>
                  <p className="mt-3 text-sm">GDPR Art. 28 Compliance: All third parties are bound by Data Processing Agreements (DPAs) ensuring confidentiality and data protection.</p>
                  <p className="mt-2"><strong>No Sale of Personal Information (CCPA/CPRA):</strong> We do not sell personal data in exchange for monetary value or equivalents.</p>
                </section>

                <section id="transfers">
                  <h2 className="text-2xl font-bold text-white mb-3">5. International Data Transfers</h2>
                  <p>Where personal data is transferred outside the EEA, Canada, or Brazil, we rely on:</p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
                    <li>Adequacy decisions where applicable</li>
                    <li>Contractual safeguards ensuring equivalent data protection</li>
                  </ul>
                </section>

                <section id="rights">
                  <h2 className="text-2xl font-bold text-white mb-3">6. User Rights</h2>
                  <div className="overflow-x-auto rounded-lg border border-gray-700">
                    <div className="min-w-[720px] grid grid-cols-3 text-sm">
                      <div className="bg-gray-800/60 px-4 py-3 font-semibold">Jurisdiction</div>
                      <div className="bg-gray-800/60 px-4 py-3 font-semibold">Rights</div>
                      <div className="bg-gray-800/60 px-4 py-3 font-semibold">How to Exercise</div>

                      <div className="px-4 py-3">GDPR (EU/EEA)</div>
                      <div className="px-4 py-3">Access, Rectification, Erasure, Restriction, Portability, Objection</div>
                      <div className="px-4 py-3">Contact DPO (<a className="text-[hsl(193,100%,50%)] hover:underline" href="mailto:info@tradesagepro.com">info@tradesagepro.com</a>)</div>

                      <div className="px-4 py-3">CCPA/CPRA (California)</div>
                      <div className="px-4 py-3">Right to Know, Delete, Correct, Opt-Out of Sale/Sharing, Limit Use of Sensitive Data</div>
                      <div className="px-4 py-3">Email DPO or use “Do Not Sell or Share My Personal Information” link</div>

                      <div className="px-4 py-3">PIPEDA (Canada)</div>
                      <div className="px-4 py-3">Access, Correction, Withdraw Consent</div>
                      <div className="px-4 py-3">Contact <a className="text-[hsl(193,100%,50%)] hover:underline" href="mailto:support@tradesagepro.com">support@tradesagepro.com</a></div>

                      <div className="px-4 py-3">LGPD (Brazil)</div>
                      <div className="px-4 py-3">Confirm processing, Access, Rectify, Erase, Portability, Revoke Consent</div>
                      <div className="px-4 py-3">Contact Encarregado (DPO)</div>
                    </div>
                  </div>
                  <p className="mt-3 text-sm">Response timeframe: Requests are addressed within 30 days (extendable by law). We may require proof of identity before processing requests.</p>
                </section>

                <section id="cookies">
                  <h2 className="text-2xl font-bold text-white mb-3">7. Cookies and Tracking Technologies</h2>
                  <p>TradeSagePro uses cookies and similar technologies for the following purposes:</p>
                  <ul className="list-disc pl-6 space-y-1 mt-2">
                    <li><strong>Strictly Necessary Cookies</strong> – Enable authentication, session security, and fraud prevention. (Cannot be disabled.)</li>
                    <li><strong>Analytics Cookies</strong> – Measure traffic and user engagement (requires consent under GDPR/LGPD).</li>
                    <li><strong>Functional Cookies</strong> – Store preferences and enhance usability.</li>
                    <li><strong>Marketing Cookies</strong> – Measure campaign effectiveness and relevance.</li>
                  </ul>
                  <p className="mt-3 text-sm">Legal Reference: GDPR Art. 6(1)(a) (Consent), Art. 6(1)(f) (Legitimate Interest); CCPA: Cal. Civ. Code §1798.140 (Advertising measurement)</p>
                  <p className="mt-2 text-sm">You can modify cookie settings anytime via the “Cookie Settings” section in the site footer.</p>
                </section>

                <section id="retention-security">
                  <h2 className="text-2xl font-bold text-white mb-3">8. Data Retention and Security</h2>
                  <ul className="list-disc pl-6 space-y-1">
                    <li><strong>Account and session data:</strong> maintained while your account is active.</li>
                    <li><strong>Payment and transaction records:</strong> retained up to 7 years (legal obligation).</li>
                    <li><strong>Security logs:</strong> stored up to 12 months.</li>
                  </ul>
                  <div className="mt-3 p-4 rounded-lg bg-[#0F1623] border border-[#1E2A44]">
                    <div className="flex items-center gap-2 font-semibold text-white mb-2"><Lock className="w-4 h-4" /> Security Measures</div>
                    <p>We employ encryption (TLS 1.2+), firewalls, access controls, anomaly detection, and periodic audits. Any data breach is managed in line with GDPR Art. 33, and users are promptly notified where legally required.</p>
                  </div>
                </section>

                <section id="minors">
                  <h2 className="text-2xl font-bold text-white mb-3">9. Data Concerning Minors</h2>
                  <p>TradeSagePro is not directed to children under 16 years old (or 13 under CCPA). We do not knowingly collect or process data from minors. If a parent or guardian believes a child has submitted data, please contact us immediately for deletion.</p>
                </section>

                <section id="updates">
                  <h2 className="text-2xl font-bold text-white mb-3">10. Updates to This Privacy Policy</h2>
                  <p>We may update this Privacy Policy periodically. Any changes will be communicated via website notice or email. Continued use of the Service constitutes acceptance of the updated terms.</p>
                </section>

                <section id="contact">
                  <h2 className="text-2xl font-bold text-white mb-3">11. Contact Information</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-[#0F1623] border border-[#1E2A44]">
                      <div className="flex items-center gap-2 font-semibold text-white mb-1"><Mail className="w-4 h-4" /> Data Protection Officer (DPO)</div>
                      <a href="mailto:info@tradesagepro.com" className="text-[hsl(193,100%,50%)] hover:underline">info@tradesagepro.com</a>
                    </div>
                    <div className="p-4 rounded-lg bg-[#0F1623] border border-[#1E2A44]">
                      <div className="flex items-center gap-2 font-semibold text-white mb-1"><Mail className="w-4 h-4" /> General Contact</div>
                      <a href="mailto:support@tradesagepro.com" className="text-[hsl(193,100%,50%)] hover:underline">support@tradesagepro.com</a>
                    </div>
                  </div>
                  <div className="mt-4 text-sm text-[hsl(210,9%,80%)]">
                    Registered Address: JINOVEX LLC – Online Service Operations, Tbilisi, Georgia (operating globally)
                  </div>
                </section>

              </div>
              </CardContent>
            </Card>
        
        </div>
      </div>
    </Layout>
  );
}
